import React from "react";
import Tooltip from "../../../../../components/common/Tooltip/Tooltip";
import { presetTip } from "./presetTip";
import "./AiSelectToolCards.css";

/** Shared outline style for this tool row — stroke 1.5, round caps/joins. */
const ICON_PROPS = {
  className: "astc-icon",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.5",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const SmartIcon = () => (
  <svg {...ICON_PROPS}>
    {/* Wand */}
    <path d="M4.5 19.5 13.8 10.2" />
    <path d="M12.9 11.1 15.6 8.4" />
    <path d="M14.7 7.5 16.5 9.3" />
    {/* Sparkles — outline 4-point stars */}
    <path d="M17.5 3.5 18.1 5.4 20 6 18.1 6.6 17.5 8.5 16.9 6.6 15 6 16.9 5.4Z" />
    <path d="M10.5 4 10.85 5.1 12 5.5 10.85 5.9 10.5 7 10.15 5.9 9 5.5 10.15 5.1Z" />
    <path d="M19.5 11 19.85 12.1 21 12.5 19.85 12.9 19.5 14 19.15 12.9 18 12.5 19.15 12.1Z" />
  </svg>
);

const BrushIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M9.5 15.5c0 1.4-1.1 2.5-2.5 2.5S4.5 16.9 4.5 15.5c0-1.8 2.5-3.5 2.5-3.5s2.5 1.7 2.5 3.5Z" />
    <path d="M7 12.2 15.2 4c.8-.8 2.1-.8 2.9 0l1.9 1.9c.8.8.8 2.1 0 2.9L11.8 17" />
    <path d="M13.8 5.4 18.6 10.2" />
  </svg>
);

const BoxIcon = () => (
  <svg {...ICON_PROPS}>
    <rect
      x="4.5"
      y="4.5"
      width="15"
      height="15"
      rx="2"
      strokeDasharray="3.5 2.5"
    />
  </svg>
);

const EraserIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M16.2 4.8 19.2 7.8c.8.8.8 2.1 0 2.9L11.5 18.4H7.8L4.8 15.4c-.8-.8-.8-2.1 0-2.9l8.5-8.5c.8-.8 2.1-.8 2.9 0Z" />
    <path d="M7.8 18.4h11.7" />
    <path d="M7.5 12.8 14.2 6.1" />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="astc-check"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.5 6.2 5 8.6 9.5 3.5" />
  </svg>
);


export const AI_MASK_PAINT_TOOLS = [
  {
    id: "brush",
    label: "Brush",
    Icon: BrushIcon,
    tip: presetTip('Brush — "Paint to select"', [
      "Paint over the area on the preview",
      "Most accurate for precise selections",
      "Use Eraser to fix overpainting",
    ]),
  },
  {
    id: "box",
    label: "Box",
    Icon: BoxIcon,
    tip: presetTip('Box — "Select area"', [
      "Drag a rectangle over the target",
      "Faster for blocks and clear regions",
      "Use Eraser to trim the selection",
    ]),
  },
  {
    id: "eraser",
    label: "Eraser",
    Icon: EraserIcon,
    tip: presetTip('Eraser — "Fix strokes"', [
      "Remove mask strokes you marked by mistake",
      "Use a smaller size for precise cleanup",
      "Switch back to Brush or Box to continue",
    ]),
  },
];

export const AI_SELECT_TOOLS = [
  {
    id: "smart",
    label: "Smart",
    Icon: SmartIcon,
    tip: presetTip('Smart — "Describe"', [
      "Describe what to remove",
      "Fastest when the subject is clear",
      "Review the result after generation",
    ]),
  },
  ...AI_MASK_PAINT_TOOLS,
];

/**
 * Icon tool cards — Smart / Brush / Box / Eraser (reference-style picker).
 */
const AiSelectToolCards = ({
  tools = AI_SELECT_TOOLS,
  selectedId = "brush",
  onSelect = () => {},
  disabled = false,
  ariaLabel = "Selection tool",
  boundaryRef = null,
}) => (
  <div
    className="astc-grid"
    role="listbox"
    aria-label={ariaLabel}
    style={{ "--astc-cols": Math.max(1, tools.length) }}
  >
    {tools.map((tool) => {
      const selected = selectedId === tool.id;
      const Icon = tool.Icon;
      const card = (
        <button
          type="button"
          role="option"
          aria-selected={selected}
          className={`astc-card${selected ? " is-selected" : ""}`}
          disabled={disabled}
          onClick={() => onSelect(tool.id)}
        >
          {selected ? (
            <span className="astc-card__badge" aria-hidden="true">
              <CheckIcon />
            </span>
          ) : null}
          <span className="astc-card__icon">
            <Icon />
          </span>
          <span className="astc-card__label">{tool.label}</span>
        </button>
      );

      if (!tool.tip) {
        return <React.Fragment key={tool.id}>{card}</React.Fragment>;
      }

      return (
        <Tooltip
          key={tool.id}
          content={tool.tip}
          position="top"
          size="panel"
          block
          flip={false}
          fillBoundary={Boolean(boundaryRef)}
          boundaryRef={boundaryRef}
        >
          {card}
        </Tooltip>
      );
    })}
  </div>
);

export default AiSelectToolCards;
